import { getApolloContext } from "@apollo/client";
import { useFlagsmith, useFlagsmithLoading } from "flagsmith/react";
import { useAtomValue, useStore } from "jotai";
import mixpanel from "mixpanel-browser";
import { useRouter } from "next/router";
import * as React from "react";
import { useContext, useEffect, useRef, useState } from "react";
import { SupportedPageEnumType } from "../@types/generated/types";
import { SessionTimeoutModal } from "../components/modals/isIdleModal/SessionTimeoutModal";
import { KATANA_E2E_AUTH } from "../constants/env";
import { MIXPANEL_CATEGORIES } from "../constants/mixpanel";
import {
  SESSION_INACTIVITY_MS,
  SESSION_WARNING_MS
} from "../constants/sessionTimeout";
import { InterfaceSessionContext, SessionContext } from "../context";
import { useFeatureFlags } from "../featureFlags/useFeatureFlags";
import { latestSessionAtom } from "../stateManagement/atoms/heartbeat.atom";
import {
  isUserUploadingAtom,
  isUserWatchingAtom,
  sessionActivityAtom
} from "../stateManagement/atoms/timeout.atom";
import useHeartbeatStore from "../stateManagement/stores/heartbeat.store";
import { Heartbeat } from "../stateManagement/types/heartbeat.types";
import { useProfile } from "./profileProvider/ProfileProvider";

const SESSION_TIME = "session_time";
const TIMER_TTL = "timer_ttl";
const TAB_ID = crypto.randomUUID();

export const useSession = () =>
  useContext<InterfaceSessionContext>(SessionContext);

export const SessionProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const { query, pathname } = useRouter();
  // Get the context procedurally since it may not exist yet
  const { client } = useContext(getApolloContext());
  const isIdle = useAtomValue(sessionActivityAtom).phase === "idle";
  const latestSession = useAtomValue(latestSessionAtom);

  const queryRef = useRef(query);
  const pageRef = useRef<SupportedPageEnumType | "landing">("landing");
  const [heartbeatSuccess, setHeartbeatSuccess] = useState<boolean>(true);
  const [time, setTime] = useState<number>(0);
  const [isActiveTab, setIsActiveTab] = useState<boolean>();

  const { token } = useProfile();
  const tabBroadcastRef = useRef<BroadcastChannel | null>(null);

  // @ts-ignore
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { sendHeartbeat, getLatestSession } = useHeartbeatStore();

  // Tabs can receive their own message so we're sending a tab id
  // and ignoring if receiving our own tab id
  const onBroadcastMessage = (ev: any) => {
    if (ev.data !== TAB_ID) {
      setIsActiveTab(false);
    }
  };

  useEffect(() => {
    let tabBroadcast: BroadcastChannel | null = null;
    try {
      tabBroadcast = new BroadcastChannel("tab");
      tabBroadcast.onmessage = onBroadcastMessage;
      tabBroadcastRef.current = tabBroadcast;
    } catch (error) {
      console.log(error);
    }

    return () => {
      tabBroadcast?.close();
      if (tabBroadcastRef.current === tabBroadcast) {
        tabBroadcastRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    queryRef.current = query;
  }, [query]);

  const onVisible = () => {
    if (document.visibilityState === "visible") {
      // letting other tabs know that this tab is active
      tabBroadcastRef.current?.postMessage(TAB_ID);
      const now = new Date().getTime();

      // fetching the latest time stored in localstorage
      // note that localstorage is shared among all tabs/windows with the
      // same sub-domain

      const currentTimeString = window.localStorage.getItem(SESSION_TIME);
      const timerTtl = window.localStorage.getItem(TIMER_TTL);

      const currentTime = currentTimeString && parseInt(currentTimeString);
      // Is the last time picked up from localStorage older than 30 min?
      const expired = (timerTtl && now > parseInt(timerTtl)) || true;

      if (currentTime && !expired) {
        setTime(currentTime);
      }
      setIsActiveTab(true);
    }
  };

  const isStudioStages = (page: string | string[] | undefined) => {
    if (!page || Array.isArray(page)) {
      return false;
    }
    return [
      SupportedPageEnumType.Transcription,
      SupportedPageEnumType.Translation,
      SupportedPageEnumType.Speech
    ].includes(page as SupportedPageEnumType);
  };

  useEffect(() => {
    onVisible();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  useEffect(() => {
    if (query.page === "videoDetails") {
      pageRef.current = SupportedPageEnumType.VideoDetails;
    } else if (query.page === "channelDetails") {
      pageRef.current = SupportedPageEnumType.ChannelDetails;
    } else if (pathname === "/studio" || pathname === "/studio-v2") {
      if (isStudioStages(query.page)) {
        pageRef.current = query.page as SupportedPageEnumType;
      } else {
        pageRef.current = SupportedPageEnumType.Studio;
      }
    } else if (pathname === "/output-review") {
      pageRef.current = SupportedPageEnumType.OutputReview;
    } else if (pathname === "/") {
      // We do not want to send a heartbeat on the landing page
      pageRef.current = "landing";
    } else {
      pageRef.current = SupportedPageEnumType.Dashboard;
    }
  }, [query, pathname]);

  useEffect(() => {
    let ch: ReturnType<typeof setInterval> | undefined;
    if (!isIdle && client && isActiveTab) {
      const event = {
        category: MIXPANEL_CATEGORIES.TIMER,
        action: "start"
      };
      if (!KATANA_E2E_AUTH) {
        mixpanel.track("timer start", event);
      }

      const callHeartbeat = async () => {
        // Skip heartbeat if disabled via environment variable
        if (process.env.NEXT_PUBLIC_PUNCH_CARD_DISABLE_HEARTBEAT === "true") {
          return;
        }

        if (pageRef.current === "landing") {
          // Do not record time on landing page ("/" route)
          return;
        } else if (
          pageRef.current === "videoDetails" &&
          !query.translatedVideoID
        ) {
          // We require tvId for videoDetails page.
          // When creating a new video, we fetch tvId using svId and lang
          // after mount. So do not send a heartbeat until we have it.
          return;
        }

        if (token.current) {
          try {
            if (!process.env.NEXT_PUBLIC_BINDER_URI) {
              throw new Error("NEXT_PUBLIC_BINDER_URI is not set");
            }

            if (!process.env.NEXT_PUBLIC_VOICES_URI) {
              throw new Error("NEXT_PUBLIC_VOICES_URI is not set");
            }

            const body: Heartbeat = {
              activityName: pageRef.current,
              modelEnvironment: process.env!.NEXT_PUBLIC_BINDER_URI
            };
            if (pageRef.current == SupportedPageEnumType.OutputReview) {
              body.modelName = "outputGroup";
              // @ts-ignore
              body.modelId = queryRef.current.outputGroupId;
            } else if (
              queryRef.current.translatedVideoID ||
              queryRef.current.translatedVideoId
            ) {
              body.modelName = "translatedVideo";

              // @ts-ignore
              body.modelId =
                queryRef.current.translatedVideoID ||
                queryRef.current.translatedVideoId;
            } else if (
              queryRef.current.translatedChannelID ||
              queryRef.current.translatedChannelId
            ) {
              body.modelName = "translatedChannel";
              // @ts-ignore
              body.modelId =
                queryRef.current.translatedChannelID ||
                queryRef.current.translatedChannelId;
            }

            sendHeartbeat(body)
              .then(() => setHeartbeatSuccess(true))
              .catch(() => setHeartbeatSuccess(false));
          } catch (error) {
            console.error(error);
          }
        }
      };
      ch = setInterval(callHeartbeat, 10000);
      // Call right away on the first load
      // If user keeps refreshing the page to try to game, binder will always return the last session without updating it
      callHeartbeat();
    }

    return () => {
      if (token.current) {
        // getLatestSession();
      }
      if (ch) {
        clearInterval(ch);
        const event = {
          category: MIXPANEL_CATEGORIES.TIMER,
          action: "idle"
        };
        if (!KATANA_E2E_AUTH) {
          mixpanel.track("timer idle", event);
        }
      }
    };
  }, [isIdle, client, isActiveTab]);

  let i: ReturnType<typeof setInterval> | undefined;
  useEffect(() => {
    if (latestSession) {
      setTime(t => Math.max(latestSession?.active_duration_sec, t));
    }

    if (!isIdle) {
      i = setInterval(() => {
        setTime(t => t + 1);
      }, 1000);
    }

    return () => {
      if (i) {
        clearInterval(i);
      }
    };
  }, [isIdle, latestSession?.sessionId]);

  useEffect(() => {
    if (isActiveTab) {
      window.localStorage.setItem(SESSION_TIME, time.toString());
      // 30 minutes ttl in milliseconds
      const ttl = new Date().getTime() + 30 * 60 * 1000;
      window.localStorage.setItem(TIMER_TTL, ttl.toString());
    }
  }, [time, isActiveTab]);

  return (
    <SessionContext.Provider
      value={{
        heartbeatSuccess,
        isIdle,
        totalTime: time
      }}
    >
      <SessionActivity />
      {children}
    </SessionContext.Provider>
  );
};

const SessionActivity = () => {
  const { profile } = useProfile();
  const flagsmith = useFlagsmith();
  const loading = useFlagsmithLoading();

  // The shared flag hook assumes a loaded profile; keep its consumers behind this boundary.
  if (
    !profile?.sub ||
    !flagsmith?.initialised ||
    loading?.isLoading !== false
  ) {
    return null;
  }

  return <ReadySessionActivity key={profile.sub} />;
};

const ReadySessionActivity = () => {
  const { query, pathname } = useRouter();
  const { session_timeout_modal } = useFeatureFlags(["session_timeout_modal"]);
  const store = useStore();
  const isWatching = useAtomValue(isUserWatchingAtom);
  const isUploading = useAtomValue(isUserUploadingAtom);
  const studio = pathname === "/studio" || pathname === "/studio-v2";
  const videoId = String(
    query.translatedVideoID ?? query.translatedVideoId ?? ""
  );
  const showWarning = studio && !!session_timeout_modal?.enabled;

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let deadline = Date.now() + SESSION_INACTIVITY_MS;
    const exempt = isWatching || isUploading;
    store.set(sessionActivityAtom, { phase: "active", deadline: null });

    const reconcile = () => {
      clearTimeout(timer);
      const current = store.get(sessionActivityAtom);
      if (exempt || current.phase === "resuming") return;
      if (showWarning && current.phase === "idle") return;

      const now = Date.now();
      if (now < deadline) {
        timer = setTimeout(reconcile, deadline - now);
        return;
      }

      if (!showWarning || now >= deadline + SESSION_WARNING_MS) {
        store.set(sessionActivityAtom, { phase: "idle", deadline: null });
        return;
      }

      if (
        current.phase !== "warning" ||
        current.deadline !== deadline + SESSION_WARNING_MS
      ) {
        store.set(sessionActivityAtom, {
          phase: "warning",
          deadline: deadline + SESSION_WARNING_MS
        });
      }
      timer = setTimeout(reconcile, deadline + SESSION_WARNING_MS - now);
    };

    const onActivity = () => {
      // Reconcile an overdue background deadline before allowing input to reset it.
      reconcile();
      const current = store.get(sessionActivityAtom);
      if (showWarning && current.phase !== "active") return;
      deadline = Date.now() + SESSION_INACTIVITY_MS;
      if (current.phase !== "active") {
        store.set(sessionActivityAtom, { phase: "active", deadline: null });
      }
      reconcile();
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") onActivity();
    };
    const unsubscribe = store.sub(sessionActivityAtom, () => {
      if (store.get(sessionActivityAtom).phase === "active") {
        deadline = Date.now() + SESSION_INACTIVITY_MS;
        reconcile();
      }
    });
    const events = [
      "mousemove",
      "mousedown",
      "resize",
      "keydown",
      "touchstart",
      "wheel"
    ];
    for (const event of events) {
      window.addEventListener(event, onActivity, { passive: true });
    }
    document.addEventListener("visibilitychange", onVisible);
    reconcile();

    return () => {
      clearTimeout(timer);
      unsubscribe();
      for (const event of events) {
        window.removeEventListener(event, onActivity);
      }
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [store, showWarning, studio, videoId, isWatching, isUploading]);

  return <SessionTimeoutModal />;
};
